import {connect} from 'react-redux';

import {
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
} from '../../../../../../srcCommon/helpers/reselectHelpers';
import {makeGetVisualBasicActivity} from '../../../../../../srcCommon/inboxes/viewer/activity/visualActivity/visualBasicActivitiesSelectors';
import {VisualBasicActivityTypesEnum} from '../../../../../../srcCommon/inboxes/viewer/activity/visualActivity/visualBasicActivity';
import {BasicActivityModel} from '../../../../../models/api/activityModel';
import {ActivitySourceVisualTypesEnum} from '../../../../../models/api/activitySourceModel';
import {AppState} from '../../../../../store/appState';
import {
  BasicViewerContainerInheritableProps,
  makeGetBasicActivityGroupState,
} from '../../../../../store/data/activities/activitiesCollapseSelectors';
import {getFlatIntegrations} from '../../../../../store/data/dataSelectors';
import {getFindTeammate} from '../../../../../store/data/teammates/lazyTeammatesSelectors';
import {getUser, getUserId} from '../../../../../store/data/teammates/teammatesSelectors';
import {CellsContext} from '../../../../../store/panels/cells/context/cellsContext';
import {navigateToLinkAction} from '../../../../../store/stateless/navigationActions';
import {BasicViewer} from './basicViewer';

/*
 * Props.
 */

interface BasicViewerContainerProps extends BasicViewerContainerInheritableProps {
  cellsContext: CellsContext;
  activity: BasicActivityModel;
}

/*
 * Selectors.
 */

function getBasicActivity(_state: AppState, props: BasicViewerContainerProps) {
  return props.activity;
}

function getBasicActivityId(_state: AppState, props: BasicViewerContainerProps) {
  return props.activity.id;
}

function makeGetSourceTeammate() {
  return createSelector([getBasicActivity, getFindTeammate], (activity, findTeammate) => {
    const {source} = activity;
    if (source.visualType !== ActivitySourceVisualTypesEnum.TEAMMATE) {
      return undefined;
    }

    return findTeammate(source.teammateId, 'BasicViewerContainer makeGetSourceTeammate');
  });
}

function makeGetIsFirstOfGroup(getGroupState: ReturnType<typeof makeGetBasicActivityGroupState>) {
  return createSelector(
    [getGroupState, getBasicActivityId],
    (groupState, activityId) => groupState?.activityId === activityId,
  );
}

function makeGetIsExpanded(getGroupState: ReturnType<typeof makeGetBasicActivityGroupState>) {
  return createSelector([getGroupState], (groupState) => !groupState || groupState.isExpanded);
}

function makeGetTopicIntegration(getVisualBasicActivity: ReturnType<typeof makeGetVisualBasicActivity>) {
  return createSelector([getFlatIntegrations, getVisualBasicActivity], (integrations, activity) => {
    if (
      !activity ||
      (activity.type !== VisualBasicActivityTypesEnum.TOPIC_ADDED &&
        activity.type !== VisualBasicActivityTypesEnum.TOPIC_CLOSED &&
        activity.type !== VisualBasicActivityTypesEnum.TOPIC_REMOVED)
    ) {
      return undefined;
    }

    const {topic} = activity;
    const integration = integrations.find((int) => int.type === topic.type);
    return integration;
  });
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<BasicViewerContainerProps>()(() => {
  const getGroupState = makeGetBasicActivityGroupState();
  const getVisualBasicActivity = makeGetVisualBasicActivity();

  return {
    user: getUser,
    userId: getUserId,
    sourceTeammate: makeGetSourceTeammate(),
    visualBasicActivity: getVisualBasicActivity,
    topicIntegration: makeGetTopicIntegration(getVisualBasicActivity),
    isFirstOfGroup: makeGetIsFirstOfGroup(getGroupState),
    isExpanded: makeGetIsExpanded(getGroupState),
  };
});

const mapDispatchToProps = makeMapDispatchToProps()(() => ({
  navigateToLink: navigateToLinkAction,
}));

export const BasicViewerContainer = connect(mapStateToProps, mapDispatchToProps)(BasicViewer);
